import React, { useEffect, useState } from 'react';
import api from '../../services/api';

interface Member {
  profile_id: number;
  full_name: string;
  member_number: string;
  dojo_name: string;
  instructor_name: string;
  current_rank_name: string;
  current_rank_index: number;
  gender: string;
  age_group_id: number;
  age_group_name: string;
  profile_status: string;
  profile_created_date: string;
}

interface Column {
  id: string;
  label: string;
  field: keyof Member;
  visible: boolean;
}

type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 50;

const DEFAULT_COLUMNS: Column[] = [
  { id: 'current_rank_name', label: 'Belt', field: 'current_rank_name', visible: true },
  { id: 'full_name', label: 'Full Name', field: 'full_name', visible: true },
  { id: 'age_group_name', label: 'Age Group', field: 'age_group_name', visible: true },
  { id: 'gender', label: 'Gender', field: 'gender', visible: true },
  { id: 'dojo_name', label: 'Dojo', field: 'dojo_name', visible: true },
  { id: 'instructor_name', label: 'Instructor(s)', field: 'instructor_name', visible: true },
  { id: 'member_number', label: 'Member No.', field: 'member_number', visible: true },
  { id: 'profile_status', label: 'Status', field: 'profile_status', visible: true },
  { id: 'profile_created_date', label: 'Joined', field: 'profile_created_date', visible: true },
];

const STORAGE_KEY = 'nask_members_columns';

const Members: React.FC = () => {
  const [allMembers, setAllMembers] = useState<Member[]>([]);
  const [filtered, setFiltered] = useState<Member[]>([]);
  const [dojos, setDojos] = useState<string[]>([]);
  const [ranks, setRanks] = useState<string[]>([]);
  const [ageGroups, setAgeGroups] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState<keyof Member>('current_rank_index');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [showColumnEditor, setShowColumnEditor] = useState(false);
  const [dragOver, setDragOver] = useState<string | null>(null);

  const loadColumns = (): Column[] => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch {}
    return DEFAULT_COLUMNS;
  };

  const [columns, setColumns] = useState<Column[]>(loadColumns());

  const saveColumns = (cols: Column[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cols));
    setColumns(cols);
  };

  const resetColumns = () => {
    localStorage.removeItem(STORAGE_KEY);
    setColumns(DEFAULT_COLUMNS);
  };

  // Multi-select filters
  const [search, setSearch] = useState('');
  const [selectedDojos, setSelectedDojos] = useState<string[]>([]);
  const [selectedRanks, setSelectedRanks] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedAgeGroups, setSelectedAgeGroups] = useState<string[]>([]);
  const [selectedGenders, setSelectedGenders] = useState<string[]>([]);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [dragCol, setDragCol] = useState<string | null>(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const response = await api.get('/reports/members/profiles?limit=2000');
        const data = response.data.data as Member[];
        setAllMembers(data);
        setFiltered(data);
        setTotalCount(data.length);

        setDojos(Array.from(new Set(data.map((m) => m.dojo_name))).sort());

        const rankMap: { [key: string]: number } = {};
        data.forEach((m) => {
          if (!rankMap[m.current_rank_name]) {
            rankMap[m.current_rank_name] = m.current_rank_index;
          }
        });
        setRanks(Object.keys(rankMap).sort((a, b) => rankMap[a] - rankMap[b]));
        setAgeGroups(
          Array.from(new Set(data.map((m) => m.age_group_name).filter(Boolean))).sort()
        );
      } catch (err) {
        setError('Failed to load members');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  useEffect(() => {
    let result = allMembers;

    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (m) =>
          (m.full_name || '').toLowerCase().includes(s) ||
          (m.member_number || '').toLowerCase().includes(s) ||
          (m.dojo_name || '').toLowerCase().includes(s) ||
          (m.instructor_name || '').toLowerCase().includes(s) ||
          (m.current_rank_name || '').toLowerCase().includes(s)
      );
    }

    if (selectedDojos.length > 0) result = result.filter((m) => selectedDojos.includes(m.dojo_name));
    if (selectedRanks.length > 0) result = result.filter((m) => selectedRanks.includes(m.current_rank_name));
    if (selectedStatuses.length > 0) result = result.filter((m) => selectedStatuses.includes(m.profile_status));
    if (selectedAgeGroups.length > 0) result = result.filter((m) => selectedAgeGroups.includes(m.age_group_name));
    if (selectedGenders.length > 0) result = result.filter((m) => selectedGenders.includes(m.gender));

    result = [...result].sort((a, b) => {
      // Use index fields for rank and age group sorting
      let av: any;
      let bv: any;

      if (sortField === 'current_rank_name') {
        av = a.current_rank_index;
        bv = b.current_rank_index;
      } else if (sortField === 'age_group_name') {
        av = a.age_group_id;
        bv = b.age_group_id;
      } else {
        av = a[sortField] !== undefined ? a[sortField] : '';
        bv = b[sortField] !== undefined ? b[sortField] : '';
      }

      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });

    setFiltered(result);
    setTotalCount(result.length);
    setPage(1);
  }, [search, selectedDojos, selectedRanks, selectedStatuses, selectedAgeGroups, selectedGenders, sortField, sortDir, allMembers]);

  const toggleSort = (field: keyof Member) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIcon = (field: keyof Member) => {
    if (sortField !== field) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  const toggleMulti = (
    value: string,
    setSelected: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const visibleColumns = columns.filter((c) => c.visible);

  const hasFilters =
    search ||
    selectedDojos.length > 0 ||
    selectedRanks.length > 0 ||
    selectedStatuses.length > 0 ||
    selectedAgeGroups.length > 0 ||
    selectedGenders.length > 0;

  const clearFilters = () => {
    setSearch('');
    setSelectedDojos([]);
    setSelectedRanks([]);
    setSelectedStatuses([]);
    setSelectedAgeGroups([]);
    setSelectedGenders([]);
    setPage(1);
  };

  const exportExcel = () => {
    setExporting(true);
    try {
      const headers = visibleColumns.map((c) => c.label);
      const rows = filtered.map((m) =>
        visibleColumns.map((c) => {
          const val = m[c.field];
          if (c.field === 'profile_created_date' && val) {
            return new Date(String(val)).toLocaleDateString('en-GB');
          }
          if (c.field === 'gender' && val) {
            return String(val).charAt(0).toUpperCase() + String(val).slice(1);
          }
          return val || '';
        })
      );

      const bom = '\uFEFF';
      const csv =
        bom +
        [headers, ...rows]
          .map((r) => r.map((v) => '"' + String(v).replace(/"/g, '""') + '"').join(','))
          .join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'NASK_Members_' + new Date().toISOString().slice(0, 10) + '.csv';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const renderCell = (m: Member, col: Column) => {
    const val = m[col.field];
    if (col.field === 'current_rank_name') {
      return <span style={styles.rankBadge}>{val || '—'}</span>;
    }
    if (col.field === 'profile_status') {
      return (
        <span style={val === 'active' ? styles.statusActive : styles.statusInactive}>
          {String(val)}
        </span>
      );
    }
    if (col.field === 'profile_created_date' && val) {
      return <>{new Date(String(val)).toLocaleDateString('en-GB')}</>;
    }
    if (col.field === 'gender' && val) {
      return <>{String(val).charAt(0).toUpperCase() + String(val).slice(1)}</>;
    }
    if (col.field === 'full_name') {
      return <span style={{ fontWeight: 500, color: '#ffffff' }}>{String(val || '—')}</span>;
    }
    if (col.field === 'instructor_name') {
      return <span style={{ fontSize: '12px' }}>{String(val || '—')}</span>;
    }
    return <>{val || '—'}</>;
  };

  // Column drag and drop
  const handleColDragStart = (id: string) => setDragCol(id);
  const handleColDragOver = (e: React.DragEvent, id: string) => {
    e.preventDefault();
    setDragOver(id);
  };
  const handleColDrop = (targetId: string) => {
    if (!dragCol || dragCol === targetId) return;
    const newCols = [...columns];
    const fromIdx = newCols.findIndex((c) => c.id === dragCol);
    const toIdx = newCols.findIndex((c) => c.id === targetId);
    const [moved] = newCols.splice(fromIdx, 1);
    newCols.splice(toIdx, 0, moved);
    saveColumns(newCols);
    setDragCol(null);
    setDragOver(null);
  };

  const MultiSelect: React.FC<{
    label: string;
    id: string;
    options: string[];
    selected: string[];
    setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  }> = ({ label, id, options, selected, setSelected }) => {
    const isOpen = openDropdown === id;
    return (
      <div style={styles.filterGroup}>
        <label style={styles.filterLabel}>{label}</label>
        <div style={{ position: 'relative' }}>
          <button
            onClick={(e) => { e.stopPropagation(); setOpenDropdown(isOpen ? null : id); }}
            style={{
              ...styles.multiSelectBtn,
              borderColor: selected.length > 0 ? '#cc0000' : '#3a3a3a',
            }}
          >
            <span>
              {selected.length === 0 ? 'All' : selected.length === 1 ? selected[0] : selected.length + ' selected'}
            </span>
            <span style={{ fontSize: '10px' }}>▼</span>
          </button>
          {isOpen && (
            <div style={styles.multiSelectDropdown} onClick={(e) => e.stopPropagation()}>
              {options.map((opt) => (
                <label key={opt} style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={selected.includes(opt)}
                    onChange={() => toggleMulti(opt, setSelected)}
                    style={{ marginRight: '8px', accentColor: '#cc0000' }}
                  />
                  {opt}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container} onClick={() => setOpenDropdown(null)}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Member Census</h1>
          <p style={styles.subtitle}>
            {loading ? 'Loading...' : totalCount.toLocaleString() + ' member' + (totalCount !== 1 ? 's' : '') + ' found'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setShowColumnEditor(!showColumnEditor)}
            style={styles.colBtn}
          >
            ⚙️ Columns
          </button>
          <button
            onClick={exportExcel}
            style={exporting ? styles.exportBtnDisabled : styles.exportBtn}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : '⬇️ Export to Excel'}
          </button>
        </div>
      </div>

      {/* Column Editor */}
      {showColumnEditor && (
        <div style={styles.columnEditor}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ color: '#ffffff', fontWeight: '600', fontSize: '14px' }}>
              Manage Columns — drag to reorder
            </span>
            <button onClick={resetColumns} style={styles.resetBtn}>
              Reset to Default
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {columns.map((col) => (
              <div
                key={col.id}
                draggable
                onDragStart={() => handleColDragStart(col.id)}
                onDragOver={(e) => handleColDragOver(e, col.id)}
                onDrop={() => handleColDrop(col.id)}
                style={{
                  ...styles.colChip,
                  borderColor: dragOver === col.id ? '#cc0000' : col.visible ? '#3a3a3a' : '#222222',
                  opacity: col.visible ? 1 : 0.5,
                }}
              >
                <span style={{ marginRight: '6px', cursor: 'grab' }}>⠿</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={col.visible}
                    onChange={() => {
                      const updated = columns.map((c) =>
                        c.id === col.id ? { ...c, visible: !c.visible } : c
                      );
                      saveColumns(updated);
                    }}
                    style={{ accentColor: '#cc0000' }}
                  />
                  <span style={{ color: '#cccccc', fontSize: '13px' }}>{col.label}</span>
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={styles.filtersCard} onClick={(e) => e.stopPropagation()}>
        <div style={styles.filtersRow}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Search</label>
            <input
              type="text"
              placeholder="Name, member no., dojo, rank..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={styles.searchInput}
            />
          </div>

          <MultiSelect label="Dojo" id="dojo" options={dojos} selected={selectedDojos} setSelected={setSelectedDojos} />
          <MultiSelect label="Belt" id="rank" options={ranks} selected={selectedRanks} setSelected={setSelectedRanks} />
          <MultiSelect label="Age Group" id="ageGroup" options={ageGroups} selected={selectedAgeGroups} setSelected={setSelectedAgeGroups} />
          <MultiSelect label="Gender" id="gender" options={['female', 'male', 'others']} selected={selectedGenders} setSelected={setSelectedGenders} />
          <MultiSelect label="Status" id="status" options={['active', 'inactive', 'deactivated']} selected={selectedStatuses} setSelected={setSelectedStatuses} />

          {hasFilters && (
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>&nbsp;</label>
              <button onClick={clearFilters} style={styles.clearBtn}>Clear All</button>
            </div>
          )}
        </div>
      </div>

      {/* Error */}
      {error && <div style={styles.errorBox}>{error}</div>}

      {/* Table */}
      {loading ? (
        <div style={styles.loadingBox}>Loading members...</div>
      ) : (
        <>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {visibleColumns.map((col) => (
                    <th
                      key={col.id}
                      style={styles.th}
                      onClick={() => toggleSort(col.field)}
                    >
                      {col.label}
                      <span style={styles.sortIcon}>{sortIcon(col.field)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((m, i) => (
                  <tr key={m.profile_id} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                    {visibleColumns.map((col) => (
                      <td key={col.id} style={styles.td}>
                        {renderCell(m, col)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {paginated.length === 0 && (
              <div style={styles.emptyBox}>No members found matching your filters.</div>
            )}
          </div>

          {/* Pagination */}
          {totalCount > PAGE_SIZE && (
            <div style={styles.pagination}>
              <span style={styles.pageInfo}>
                {'Showing ' +
                  ((page - 1) * PAGE_SIZE + 1).toLocaleString() +
                  '–' +
                  Math.min(page * PAGE_SIZE, totalCount).toLocaleString() +
                  ' of ' +
                  totalCount.toLocaleString() +
                  ' members'}
              </span>
              <div style={styles.pageButtons}>
                <button onClick={() => setPage(1)} disabled={page === 1} style={page === 1 ? styles.pageBtnDisabled : styles.pageBtn}>First</button>
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} style={page === 1 ? styles.pageBtnDisabled : styles.pageBtn}>Prev</button>
                <span style={styles.pageNumber}>{'Page ' + page + ' of ' + totalPages}</span>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={page === totalPages ? styles.pageBtnDisabled : styles.pageBtn}>Next</button>
                <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={page === totalPages ? styles.pageBtnDisabled : styles.pageBtn}>Last</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: '32px', fontFamily: "'Segoe UI', sans-serif" },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { color: '#ffffff', fontSize: '24px', fontWeight: '700', margin: '0 0 4px 0' },
  subtitle: { color: '#888888', fontSize: '14px', margin: '0' },
  exportBtn: { backgroundColor: '#1a3a1a', border: '1px solid #22c55e', color: '#22c55e', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' },
  exportBtnDisabled: { backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', color: '#666666', padding: '10px 20px', borderRadius: '8px', cursor: 'not-allowed', fontSize: '14px' },
  colBtn: { backgroundColor: '#1a1a2e', border: '1px solid #3a3a6a', color: '#6b8cff', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  columnEditor: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '12px', padding: '20px', marginBottom: '16px' },
  colChip: { backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'center', cursor: 'grab' },
  resetBtn: { backgroundColor: 'transparent', border: '1px solid #555555', color: '#888888', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' },
  filtersCard: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '12px', padding: '20px', marginBottom: '24px' },
  filtersRow: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '140px' },
  filterLabel: { color: '#888888', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  searchInput: { backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '10px 16px', color: '#ffffff', fontSize: '14px', outline: 'none', minWidth: '220px' },
  multiSelectBtn: { backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '10px 16px', color: '#ffffff', fontSize: '14px', cursor: 'pointer', textAlign: 'left', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  multiSelectDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '8px', zIndex: 100, maxHeight: '200px', overflowY: 'auto', marginTop: '4px', minWidth: '180px' },
  checkboxLabel: { display: 'flex', alignItems: 'center', color: '#cccccc', fontSize: '13px', padding: '6px 8px', cursor: 'pointer', borderRadius: '4px' },
  clearBtn: { backgroundColor: '#2a0000', border: '1px solid #cc0000', color: '#cc0000', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap' },
  errorBox: { backgroundColor: '#2d1a1a', border: '1px solid #cc3333', color: '#ff6666', padding: '12px 16px', borderRadius: '8px', marginBottom: '24px' },
  loadingBox: { color: '#888888', textAlign: 'center', padding: '48px' },
  tableWrapper: { overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 360px)', borderRadius: '12px', border: '1px solid #2a2a2a', marginBottom: '16px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { backgroundColor: '#1a1a1a', color: '#888888', padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #2a2a2a', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1, cursor: 'pointer', userSelect: 'none' },
  sortIcon: { color: '#cc0000', fontSize: '10px' },
  trEven: { backgroundColor: '#0f0f0f' },
  trOdd: { backgroundColor: '#111111' },
  td: { padding: '12px 16px', color: '#cccccc', borderBottom: '1px solid #1a1a1a', whiteSpace: 'nowrap' },
  rankBadge: { backgroundColor: '#1a1a2e', color: '#6b8cff', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  statusActive: { backgroundColor: '#0d2d1a', color: '#22c55e', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  statusInactive: { backgroundColor: '#2d1a0d', color: '#f59e0b', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  emptyBox: { color: '#888888', textAlign: 'center', padding: '48px', backgroundColor: '#0f0f0f' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', flexWrap: 'wrap', gap: '12px' },
  pageInfo: { color: '#888888', fontSize: '14px' },
  pageButtons: { display: 'flex', gap: '8px', alignItems: 'center' },
  pageBtn: { backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', color: '#ffffff', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  pageBtnDisabled: { backgroundColor: '#111111', border: '1px solid #222222', color: '#444444', padding: '8px 14px', borderRadius: '6px', cursor: 'not-allowed', fontSize: '13px' },
  pageNumber: { color: '#888888', fontSize: '13px', padding: '0 8px' },
};

export default Members;